import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Box,
  styled,
  useTheme,
} from '@mui/material';
import VisorPDF from '../../components/Visor/VisorPDF';
import { documentsActions, fileActions, addTitleLegoCurrent } from '../../store/actions';
import { b64toUrl, GetMimeType, IsDevice, coreFunctions } from '../../utils';
import { useTranslation } from 'react-i18next';
import RejectDocuments from './RejectDocument';
import {
  ButtonsLinedCustom,
  CustomLoadingOverlay,
  GenericContainer,
  GenericContainerForm,
} from '../../components';

const LabelCustom = styled(FormControlLabel)({
  margin: '0px !important',
  '& .MuiFormControlLabel-root, label': {
    margin: '0px !important',
  },
  '& .PrivateSwitchBase-root': {
    padding: '5px !important',
  },
});

export default function SignFEAViewerCheckCarrusel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const ctrlCheckRef = useRef();

  // Redux state
  const file = useSelector((state) => state.FileInfo);
  const pdfDoc = useSelector((state) => state.Document);

  // Local state
  const [containerHeight, setContainerHeight] = useState(80);
  const [fileUrl, setFileUrl] = useState(null);
  const [documentList, setDocumentList] = useState([]);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [checkState, setCheckState] = useState(false);
  const [activeContinueButton, setActiveContinueButton] = useState(false);
  const [previous, setPrevious] = useState(false);
  const [isPrevDoc, setIsPrevDoc] = useState(false);

  const [loadView, setLoadView] = useState(false);

  useEffect(() => {
    dispatch(addTitleLegoCurrent('Firma de documentos'));
    dispatch(fileActions.GetFileInfo(location.state.folioId));
    sessionStorage.setItem('properties', null);
    setLoadView(true);
    handleResize();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (file.items) {
      processDocumentList(file.items);
      setLoadView(false);
      setActiveContinueButton(false);
    }
  }, [file]);

  useEffect(() => {
    console.log('pdfDoc:', pdfDoc);
    setLoadView(pdfDoc.loading);
    if (pdfDoc.items) {
      setFileUrl(null);
      const pdfB64 = pdfDoc.items.B64Content;
      if (pdfB64) {
        (async () => {
          const blobUrl = await b64toUrl(`${GetMimeType('.pdf')},${pdfB64}`);
          setFileUrl(blobUrl);
          setLoadView(false);
        })();
      }
    }
  }, [pdfDoc]);

  useEffect(() => {
    if (documentList.length === 0) return;
    sessionStorage.setItem('properties', JSON.stringify(documentList));
    const pendientesCapturar = documentList.filter((item) => item.Captured === null);

    if (pendientesCapturar.length > 0) {
      const { Id = 0, Name = 'Firma de documentos' } = pendientesCapturar[0]?.Document || {};
      setCurrentDocument(pendientesCapturar[0]?.Document);
      dispatch(documentsActions.DownloadDocument(Id));
      dispatch(addTitleLegoCurrent(Name));

      if (checkState && ctrlCheckRef.current) {
        ctrlCheckRef.current.click();
      }
    } else {
      setActiveContinueButton(true);
      handleAcceptDocument();
    }
  }, [documentList]);

  const handleResize = () => {
    setContainerHeight(window.innerHeight > 900 ? 75 : 70);
  };

  const processDocumentList = (data) => {
    const documentTypes = data.Stage.DocumentalCategory.DocumentalGroups.flatMap(
      (group) => group.DocumentTypes?.filter((item) => item.Document) || [],
    );
    const processedDocs = documentTypes.map((doc, index) => ({
      ...doc,
      CurrentIndex: index,
      Captured: null,
      SendSign: null,
    }));
    setDocumentList(processedDocs);
  };

  const handleAcceptDocument = () => {
    if (!activeContinueButton) {
      setPrevious(true);
      handleNext();
    } else {
      navigateNextStage();
    }
  };

  const handleNext = () => {
    const currentIndex = documentList.findIndex((doc) => doc.Document.Id === currentDocument?.Id);
    let newArr = [...documentList];
    newArr[currentIndex].Captured = true;
    setDocumentList(newArr);

    if (currentIndex < documentList.length - 1) {
      setCurrentDocument(documentList[currentIndex + 1].Document);
      dispatch(documentsActions.DownloadDocument(documentList[currentIndex + 1].Document.Id));
    }
  };

  const handlePrevious = () => {
    const currentIndex = documentList.findIndex((doc) => doc.Document.Id === currentDocument?.Id);

    const prevDoc = documentList[currentIndex - 1];

    if (currentIndex > 0) {
      const {
        Document: { Id, Name },
        Captured,
      } = prevDoc;
      setCurrentDocument(documentList[currentIndex - 1].Document);
      dispatch(documentsActions.DownloadDocument(documentList[currentIndex - 1].Document.Id));

      dispatch(addTitleLegoCurrent(Name ?? 'Firma de documentos'));
      setIsPrevDoc(true);
      // Ejecutar el control del check si ya capturo
      if (Captured && !checkState) ctrlCheckRef.current.click();
    }
  };

  const navigateNextStage = () => {
    const { Stage } = file.items;

    let nextComponent = 'AdvancedSignature';
    coreFunctions.DoNextStage({
      navigate,
      nextComponent,
      State: {
        stageName: Stage.Name,
        stageId: Stage.Id,
        folioId: file.items.Id,
        ListDocuments: documentList,
      },
    });
  };

  return (
    <GenericContainer>
      <Typography variant="subtitle1">{t('legal_read')}</Typography>
      <GenericContainerForm dynamicSize={window.innerHeight > 900 ? 300 : 315}>
        <VisorPDF pdfUrl={fileUrl} porcentaje={containerHeight} />
      </GenericContainerForm>
      <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
        {!activeContinueButton && (
          <LabelCustom
            value={'TextCheck'}
            control={
              <Checkbox
                ref={ctrlCheckRef}
                color="primary"
                checked={checkState}
                onChange={(e) => setCheckState(e.target.checked)}
                value={'TextCheck'}
              />
            }
            label={<Typography variant="subtitle2">{t('legal_read_auth')}</Typography>}
          />
        )}
        <ButtonsLinedCustom
          disabledConfirm={!checkState && !activeContinueButton}
          ConfirmButtonText={
            isPrevDoc ? t('general_next_2') : checkState ? t('general_accept') : t('general_next')
          }
          onClickConfirm={isPrevDoc ? handleNext : handleAcceptDocument}
          disabledCancel={!previous}
          CancelButtonText={t('general_prev')}
          onClickCancel={previous ? handlePrevious : null}
          customControl={
            <RejectDocuments documentId={currentDocument?.Id} fileId={file.items?.Id} height={35} />
          }
        />
      </Box>
      {loadView ? (
        <CustomLoadingOverlay active={false} text={t('general_loading_message')} />
      ) : null}
    </GenericContainer>
  );
}
